import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageTherapyNotHelping = makeArticle({
  slug: "what-if-therapy-isnt-helping-depression",
  title: "What If Therapy Isn't Helping My Depression? | Neuro Wellness",
  description:
    "If therapy isn't helping your depression, learn why, what to do, and what other options exist — including TMS, SAINT®, SPRAVATO®, and medication optimization.",
  canonical: "/learn/what-if-therapy-isnt-helping-depression",
  h1: "What Can I Do If Therapy Isn't Helping My Depression?",
  breadcrumbLabel: "Therapy Isn't Helping",
  shortAnswer:
    "If therapy isn't helping your depression, several factors may be involved: the type of therapy may not be the right fit, the therapist may not be the right match, therapy alone may be insufficient for your depression severity, or an underlying medical or medication issue may need addressing. Options include trying a different therapy approach, adding medication, or considering treatments like TMS, SAINT® accelerated TMS, or SPRAVATO®. Discuss your progress with your therapist and consider a psychiatric evaluation.",
  keyTakeaways: [
    "Therapy not helping doesn't mean nothing will work.",
    "Different therapy types work for different people.",
    "Combining therapy with medication or other treatments may be more effective.",
    "TMS, SAINT®, and SPRAVATO® are options beyond therapy and medication.",
    "Discuss your progress with your therapist and consider a psychiatric evaluation.",
  ],
  sections: [
    {
      heading: "Why Therapy May Not Be Helping",
      paragraphs: [
        "Therapy can be ineffective for several reasons: the specific therapy type may not match your needs (CBT, psychodynamic, interpersonal, and behavioral activation all work differently), the therapist-client fit may not be right, therapy alone may be insufficient for moderate-to-severe depression, or an underlying condition (like bipolar disorder or a medical issue) may need different treatment.",
        "Therapy is most effective for mild-to-moderate depression. For moderate-to-severe depression, combining therapy with medication or other treatments is often more effective than therapy alone.",
      ],
    },
    {
      heading: "What to Do If Therapy Isn't Working",
      paragraphs: ["If therapy isn't helping, consider these steps:"],
      list: [
        "Talk to your therapist — discuss your progress and whether a different approach might help",
        "Try a different therapy type — CBT, behavioral activation, interpersonal therapy, or DBT",
        "Consider a psychiatric evaluation — to assess whether medication or other treatments are needed",
        "Add medication — combining therapy with antidepressants is often more effective",
        "Explore TMS, SAINT®, or SPRAVATO® — for depression that hasn't responded to therapy and medication",
      ],
    },
    {
      heading: "When to Consider Treatments Beyond Therapy",
      paragraphs: [
        "If you've tried therapy (especially multiple types or therapists) and your depression hasn't improved adequately, treatments beyond therapy may be appropriate. TMS is FDA-cleared for depression that hasn't responded to medication. SAINT® is an accelerated 5-day protocol for treatment-resistant depression. SPRAVATO® is FDA-approved for treatment-resistant depression. These are options to discuss with a psychiatrist.",
      ],
    },
    {
      heading: "Therapy Still Matters",
      paragraphs: [
        "Even if therapy alone hasn't resolved your depression, therapy remains valuable alongside other treatments. Therapy can help you develop coping skills, process emotions, and address patterns that contribute to depression. Many patients continue therapy during and after TMS, SAINT®, or SPRAVATO® treatment. Combining approaches is often more effective than any single treatment alone.",
      ],
    },
  ],
  faqs: [
    {
      question: "What if therapy isn't helping my depression?",
      answer:
        "Talk to your therapist about your progress. Consider trying a different therapy type, adding medication, or getting a psychiatric evaluation. For depression that hasn't responded to therapy and medication, TMS, SAINT®, and SPRAVATO® are options. Combining treatments is often more effective than any single approach.",
    },
    {
      question: "Why isn't my therapy working for depression?",
      answer:
        "Therapy may not be working because the therapy type isn't the right fit, the therapist isn't the right match, therapy alone is insufficient for your depression severity, or an underlying condition needs different treatment. Discuss your progress with your therapist and consider a psychiatric evaluation.",
    },
    {
      question: "Should I stop therapy if it isn't helping?",
      answer:
        "Not necessarily. Talk to your therapist first. You might try a different therapy type or add medication. Even if therapy alone hasn't resolved your depression, it can be valuable alongside other treatments like TMS or medication. Combining approaches is often more effective.",
    },
    {
      question: "Can TMS help if therapy hasn't worked?",
      answer:
        "TMS is FDA-cleared for depression that hasn't responded to antidepressant medication. If therapy alone hasn't been enough, TMS may be an option — especially if medication hasn't worked either. A psychiatric evaluation can determine if TMS is appropriate for your situation.",
    },
    {
      question: "Is therapy or medication better for depression?",
      answer:
        "Neither is universally better. For mild depression, therapy may be sufficient. For moderate-to-severe depression, combining therapy with medication is often more effective. For treatment-resistant depression, options like TMS, SAINT®, and SPRAVATO® may be appropriate. The best approach depends on your individual circumstances.",
    },
  ],
  sources: [
    {
      citation:
        "Cuijpers P, et al. The effects of psychotherapy for adult depression are overestimated: a meta-analysis of study quality and funnel plot asymmetry. Psychol Med. 2010;40(2):211-223.",
      url: "https://pubmed.ncbi.nlm.nih.gov/19490743/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
